"use client";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import useLogin from "@/components/hooks/login/useLogin.hook";
import { Spinner } from "@/components/ui/spinner";
import Link from "next/link";
import TwoFAModal from "./two-fa-modal";
import { useTranslation } from "@/hooks/useTranslation.hook";

interface LoginFormData {
  email: string;
  password: string;
}

const LoginForm = () => {
  const {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
    recaptchaLoaded,
    // 2FA related
    showTwoFAModal,
    twoFAEmail,
    isVerifying2FA,
    isResending2FA,
    resendDisabled,
    resendCountdown,
    handleVerify2FA,
    handleResend2FA,
    closeTwoFAModal,
  } = useLogin();

  const { t } = useTranslation();

  return (
    <>
      <div className="space-y-2 text-center mb-4 sm:mb-6">
        <h2 className="text-primary text-2xl sm:text-3xl lg:text-4xl font-semibold font-notoSans">
          アカウントにログイン
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          プロジェクト管理を始めましょう
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-3 sm:space-y-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>メールアドレス</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="demo@opentakt.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage t={t} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>パスワード</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="パスワードを入力"
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage t={t} />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={loginLoading || !recaptchaLoaded}
          >
            {loginLoading && <Spinner size="sm" className="mr-2" />}
            ログイン
          </Button>
          <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Link href="/forgot-password" className="text-primary">
              {t("Forgot password ?")}
            </Link>
          </p>
        </form>
      </Form>

      <TwoFAModal
        isOpen={showTwoFAModal}
        onVerify={handleVerify2FA}
        onResend={handleResend2FA}
        onClose={closeTwoFAModal}
        isVerifying={isVerifying2FA}
        isResending={isResending2FA}
        resendDisabled={resendDisabled}
        countdown={resendCountdown}
      />
    </>
  );
};

export default LoginForm;
